import { Loader2Icon } from "lucide-react"
import { useRef } from "react"
import { Button } from "@/primitives/button.tsx"
import { Disc, type PersonShown } from "./identity.tsx"

/*
 * A Member's own photo as they change it: on their Profile page and in the first-sign-in check.
 */

/** What a photo may be, as the file picker offers it: an iPhone's HEIC is turned into JPEG first. */
const accepted = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"

const busyWords = { uploading: "Uploading…", removing: "Removing…" } as const

/**
 * Their avatar at 64px, beside Upload photo (Replace once they have one) and Remove, and what a
 * photo may be. Choosing a file hands it to `onChoose`, which uploads it; `busy` says what is under
 * way, which both buttons wait on.
 */
export function ProfilePhoto({
  person,
  hasPhoto,
  busy,
  onChoose,
  onRemove,
}: {
  person: PersonShown
  hasPhoto: boolean
  busy?: keyof typeof busyWords
  onChoose: (file: File) => void
  onRemove: () => void
}) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <div className="flex items-center gap-4">
      <Disc person={person} size={64} />
      <div className="min-w-0 space-y-2">
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            disabled={busy !== undefined}
            onClick={() => input.current?.click()}
          >
            {busy === "uploading" && (
              <Loader2Icon data-icon="inline-start" className="animate-spin" />
            )}
            {busy === "uploading"
              ? busyWords.uploading
              : hasPhoto
                ? "Replace photo"
                : "Upload photo"}
          </Button>
          {hasPhoto && (
            <Button variant="ghost" size="sm" disabled={busy !== undefined} onClick={onRemove}>
              {busy === "removing" ? busyWords.removing : "Remove"}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          JPEG, PNG, WebP or an iPhone photo, up to 10 MB. It's cropped square.
        </p>
      </div>
      <input
        ref={input}
        type="file"
        accept={accepted}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ""
          if (file) onChoose(file)
        }}
      />
    </div>
  )
}
